import { CircularProgress, Stack, Typography } from '@mui/material';
import { api, TPerson, TValidationResults } from '@verifime/api-definition';
import tokens from '@verifime/design-tokens';
import { DOCUMENT_TYPES, stringUtils, usePolling } from '@verifime/utils';
import { Dispatch, memo, SetStateAction, useEffect, useRef, useState } from 'react';

import { TDisplayMode } from '../modeDeterminer';
import { IdScanComplete } from './IdScanComplete';
import { OcrDocumentEditor } from './OcrDocumentEditor';
import { OcrMedicareCard } from './OcrMedicareCard';
import { buildOcrData, TOcrData, TSupportedOcrDocumentType } from './utils';

const LoadingIndicator = () => {
  return (
    <Stack direction="row" gap={tokens.spacingBase}>
      <CircularProgress color="inherit" size={20} />
      <Typography>Loading OCR...</Typography>
    </Stack>
  );
};

type OcrEditorProps = Readonly<{
  customer: TPerson;
  documentType: TSupportedOcrDocumentType;
  numUploadedFiles: number;
  onFormChange?: (isValid: boolean, data: TOcrData) => void;
  uploadRequired?: boolean;
  displayMode: TDisplayMode;
  setDisplayMode?: Dispatch<SetStateAction<TDisplayMode>>;
  validationResults: TValidationResults;
}>;

export const OcrEditor = memo(
  ({
    customer,
    documentType,
    onFormChange,
    numUploadedFiles,
    uploadRequired = true,
    displayMode,
    setDisplayMode,
    validationResults,
  }: OcrEditorProps) => {
    const [ocrData, setOcrData] = useState<TOcrData | null>(null);
    const [isLoadingOcr, setIsLoadingOcr] = useState(true);
    const [ocrFilesCheckStatus, setOcrFilesCheckStatus] = useState<'NONE' | 'NO_FILES' | 'READY'>(
      'NONE',
    );
    const userTypedDataRef = useRef(ocrData);

    useEffect(() => {
      setOcrData(null);
    }, [documentType]);

    const { startPolling, stopPolling } = usePolling({
      pollingAction: () => {
        api
          .getV1fileEntityIdDocType({
            params: { docType: documentType, entityId: customer.id },
          })
          .then((data) => {
            if (data.itemList?.length > 0) {
              stopPolling();
              setOcrFilesCheckStatus('READY');
            }
          })
          .catch((error) => console.error(error));
      },
      pollingIntervalInMs: 3000,
      maxRetry: 3,
      onMaxRetryReached: () => {
        setOcrFilesCheckStatus('NO_FILES');
        setIsLoadingOcr(false);
      },
    });

    useEffect(() => {
      if (numUploadedFiles < 1) {
        return;
      }
      startPolling();
    }, [numUploadedFiles]);

    useEffect(() => {
      const documentFieldName = stringUtils.lowerCaseFirstLetter(documentType);
      if (documentFieldName in customer) {
        const ocrData = buildOcrData(documentType, customer[documentFieldName]);
        if (documentType === 'MedicareCard') {
          ocrData.medicareCardOwners = {};
        }
        userTypedDataRef.current = ocrData;
        setOcrData(ocrData);
        setIsLoadingOcr(false);
        stopPolling();
      }
    }, [documentType, customer]);

    useEffect(() => {
      const loadOcr = async () => {
        setIsLoadingOcr(true);
        // We only support these documents for now
        const typedDocumentType = documentType as 'Passport' | 'DriverLicence' | 'MedicareCard';

        api
          .getV1documentscanresultcustomerCustomerIdidentityDocumentDocumentTypescan({
            params: { customerId: customer.id, documentType: typedDocumentType },
          })
          .then((data) => {
            const isSameTypeDocument = data?.type ? documentType === data?.type : true;

            if (!isSameTypeDocument) {
              setDisplayMode('fileUploaderAndFormEditor');
              return;
            }

            const ocrData = calculateFormFieldsValues({
              loadedOcrData: data,
              initialOcrData: userTypedDataRef.current,
              documentType,
            });
            setOcrData(ocrData);
          })
          .catch((error) => {
            if (displayMode === 'fileUploaderAndFormEditor') {
              setOcrData(userTypedDataRef.current);
              console.error(
                'No need error handling for display mode: [fileUploaderAndFormEditor] ',
              );
              return;
            }
            setDisplayMode('fileUploaderAndFormEditor');
            console.error('Error loading OCR data: ', error);
          })
          .finally(() => {
            setIsLoadingOcr(false);
          });
      };

      // We don't support scan other identity document for now
      if (documentType === 'OtherIdentityDocument') {
        return;
      }

      if (numUploadedFiles < 1) {
        if (uploadRequired) {
          setOcrData(null);
        }
        setIsLoadingOcr(false);
        stopPolling();
        return;
      }

      if (ocrFilesCheckStatus !== 'READY') {
        return;
      }

      loadOcr();
    }, [
      customer,
      documentType,
      ocrFilesCheckStatus,
      uploadRequired,
      numUploadedFiles,
      displayMode,
    ]);

    if (!customer || !documentType) {
      return;
    }

    if (!ocrData && isLoadingOcr) {
      return <LoadingIndicator />;
    }

    // Allow manual entry when uploadRequired is false or displayMode is fileUploaderAndFormEditor when OCR data is not available
    if (!ocrData) {
      if (!uploadRequired || displayMode === 'fileUploaderAndFormEditor') {
        const userTypedOcrData = userTypedDataRef.current;
        // Show manual entry form only when scan is not required AND files are uploaded

        return (
          <Stack gap={tokens.spacingBase}>
            <Typography variant="h6">
              Enter your {DOCUMENT_TYPES[documentType]?.label.toLowerCase()} details
            </Typography>
            <Typography variant="body1">
              Please carefully enter the details exactly as they appear on your{' '}
              {DOCUMENT_TYPES[documentType]?.label.toLowerCase()}.
            </Typography>
            {documentType === 'MedicareCard' ? (
              <OcrMedicareCard
                initialData={userTypedOcrData}
                documentType={documentType}
                onFormChange={(isValid, data) => {
                  userTypedDataRef.current = data;
                  onFormChange(isValid, data);
                }}
                validationResults={validationResults}
              />
            ) : (
              <OcrDocumentEditor
                customer={customer}
                initialData={userTypedOcrData}
                documentType={documentType}
                onFormChange={(isValid, data) => {
                  userTypedDataRef.current = data;
                  onFormChange(isValid, data);
                }}
                validationResults={validationResults}
              />
            )}
          </Stack>
        );
      }

      return;
    }

    return (
      <Stack gap={tokens.spacingBase}>
        <IdScanComplete />
        {isLoadingOcr && <LoadingIndicator />}
        {documentType === 'MedicareCard' ? (
          <OcrMedicareCard
            initialData={ocrData}
            documentType={documentType}
            onFormChange={(isValid, data) => {
              userTypedDataRef.current = data;
              onFormChange(isValid, data);
            }}
            validationResults={validationResults}
          />
        ) : (
          <OcrDocumentEditor
            customer={customer}
            initialData={ocrData}
            documentType={documentType}
            onFormChange={(isValid, data) => {
              userTypedDataRef.current = data;
              onFormChange(isValid, data);
            }}
            validationResults={validationResults}
          />
        )}
      </Stack>
    );
  },
);

OcrEditor.displayName = 'OcrEditor';

function calculateFormFieldsValues({
  loadedOcrData,
  initialOcrData,
  documentType,
}: {
  loadedOcrData: TOcrData;
  initialOcrData: TOcrData;
  documentType: TSupportedOcrDocumentType;
}): TOcrData {
  if (!initialOcrData) {
    return loadedOcrData;
  }

  const documentFieldName = stringUtils.lowerCaseFirstLetter(documentType);

  const typedInitialData = initialOcrData[documentFieldName] as Record<string, any>;
  const typedLoadedData = loadedOcrData[documentFieldName] as Record<string, any>;

  Object.entries(typedInitialData).forEach(([k, v]) => {
    if (!v) {
      typedInitialData[k] = typedLoadedData[k];
    }
  });

  if (documentType === 'MedicareCard') {
    return typedInitialData;
  }

  initialOcrData[documentFieldName] = typedInitialData;
  return initialOcrData;
}
